import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { placementsAPI, studentsAPI, restaurantsAPI, orphanAPI, assessmentRulesAPI, cyclesAPI } from '../lib/api';
import { byStudentName, byName } from '../lib/sort';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RequiredLabel } from '../components/RequiredLabel';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogActionBar } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Building2, ChevronDown, ChevronRight, ChevronLeft, Users, Pencil, Trash2, MessageSquare, Download, FileSpreadsheet, FileText, FileSignature, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { downloadBlob, exportTableToPdf } from '../lib/exportUtils';
import PlacementContractsPanel from '../components/ContractsPanel';
import BulkLettersDialog from '../components/BulkLettersDialog';
import OrphanPlacementsDialog from '../components/OrphanPlacementsDialog';
import PlacementDetailsDialog from '../components/PlacementDetailsDialog';
import CyclePickerGrid from '../components/CyclePickerGrid';
import SearchableSelect from '../components/SearchableSelect';

const Placements = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  const cycleParam = searchParams.get('cycleId') || ''; // '' = grid view, '<id>' = drilled in, 'unassigned' = legacy bucket
  const [placements, setPlacements] = useState([]);
  const [students, setStudents] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [expandedRestaurants, setExpandedRestaurants] = useState({});
  const [formData, setFormData] = useState({
    student_id: '',
    restaurant_id: '',
    cycle_id: '',
    position: '',
    start_date: '',
    end_date: '',
    notes: '',
  });
  const [filterYear, setFilterYear] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterCycle, setFilterCycle] = useState('');
  const [cycles, setCycles] = useState([]);
  const [contractsPlacement, setContractsPlacement] = useState(null);
  const [bulkLettersOpen, setBulkLettersOpen] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [detailsPlacementId, setDetailsPlacementId] = useState(null);
  const [assessmentDates, setAssessmentDates] = useState([]); // ['YYYY-MM-DD', ...]
  const [assessmentDefaults, setAssessmentDefaults] = useState({ count: 4, interval: 30 });
  const [showAdvanced, setShowAdvanced] = useState(false); // Placement Modal 2.0: collapse rarely-edited fields
  const [saving, setSaving] = useState(false); // covers both Save and Save & Send

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pRes, sRes, rRes, cRes] = await Promise.all([
        placementsAPI.getAll(), studentsAPI.getAll(), restaurantsAPI.getAll(), cyclesAPI.list(),
      ]);
      setPlacements(pRes.data);
      setStudents(sRes.data);
      setRestaurants(rRes.data);
      setCycles(cRes.data || []);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
    // Fire orphan check in the background; failure is non-fatal (e.g. non-admin)
    orphanAPI.listPlacements()
      .then((res) => setOrphanCount((res.data || []).length))
      .catch(() => setOrphanCount(0));
  };

  // Placement Modal 2.0: create placement, optionally also send the ack link.
  const handleSubmit = async ({ sendAck = false } = {}) => {
    if (!formData.student_id || !formData.restaurant_id || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields'); return;
    }
    setSaving(true);
    try {
      if (editingPlacement) {
        await placementsAPI.update(editingPlacement.id, formData);
        toast.success('Placement updated');
      } else {
        const filled = assessmentDates.filter((d) => (d || '').trim());
        const payload = { ...formData };
        if (filled.length > 0) payload.assessment_due_dates = filled;
        const res = await placementsAPI.create(payload);
        const created = res?.data;
        if (sendAck && created?.id) {
          try {
            await placementsAPI.sendAck(created.id);
            toast.success(`Placement created + ack link sent to student`);
          } catch (e) {
            toast.warning(`Placement created, but sending the ack link failed: ${e.response?.data?.detail || e.message}`);
          }
        } else {
          toast.success(`Placement created with ${filled.length || assessmentDefaults.count} assessments`);
        }
      }
      setIsModalOpen(false); resetForm(); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save placement');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this placement?')) return;
    try {
      await placementsAPI.delete(id);
      toast.success('Placement deleted'); fetchData();
    } catch { toast.error('Failed to delete placement'); }
  };

  const handleEdit = (placement) => {
    setEditingPlacement(placement);
    setFormData({
      student_id: placement.student_id,
      restaurant_id: placement.restaurant_id,
      position: placement.position || '',
      start_date: placement.start_date,
      end_date: placement.end_date,
      notes: placement.notes || '',
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingPlacement(null);
    // Auto-pick the single active cycle if there's exactly one — saves the admin a click.
    const activeCycles = (cycles || []).filter(c => c.status !== 'archived' && c.status !== 'closed');
    const soloCycle = activeCycles.length === 1 ? activeCycles[0] : null;
    setFormData({
      student_id: '',
      restaurant_id: '',
      cycle_id: soloCycle?.id || '',
      position: '',
      start_date: soloCycle?.start_date || '',
      end_date: soloCycle?.end_date || '',
      notes: '',
    });
    setFilterYear(soloCycle?.year_of_study || '');
    setFilterCourse(soloCycle?.course_length || '');
    setAssessmentDates([]);
    setAssessmentDefaults({ count: 4, interval: 30 });
    setShowAdvanced(false);
  };

  // Students already placed (active or pending) in the currently selected cycle.
  // Used to grey them out in the dropdown so an admin can't accidentally re-place them.
  const placedStudentIdsInCycle = React.useMemo(() => {
    if (!formData.cycle_id) return new Set();
    return new Set(
      placements
        .filter(p => p.cycle_id === formData.cycle_id && (p.status === 'active' || p.status === 'pending'))
        .map(p => p.student_id)
    );
  }, [placements, formData.cycle_id]);

  // Live slot counts per restaurant IN THIS CYCLE. Restaurants with zero remaining
  // slots are disabled in the picker to prevent overbooking. Scoped to the
  // currently-selected cycle so cross-cycle placements at the same restaurant
  // don't wrongly consume slots.
  const restaurantSlots = React.useMemo(() => {
    const map = {};
    restaurants.forEach(r => {
      const filled = placements.filter(p =>
        p.restaurant_id === r.id
        && (p.status === 'active' || p.status === 'pending')
        && (!formData.cycle_id || p.cycle_id === formData.cycle_id)
      ).length;
      map[r.id] = { filled, total: r.available_positions || 0, open: Math.max((r.available_positions || 0) - filled, 0) };
    });
    return map;
  }, [restaurants, placements, formData.cycle_id]);

  // Whenever the admin picks a student + a start_date on a NEW placement, resolve
  // the applicable Assessment Rule and pre-fill N date pickers with
  // start + interval*(i+1). The admin can then customise each date, add/remove
  // rows on the fly.
  useEffect(() => {
    if (editingPlacement) return; // schedule is not editable during edit
    if (!formData.student_id || !formData.start_date) {
      setAssessmentDates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await assessmentRulesAPI.resolveForStudent(formData.student_id);
        if (cancelled) return;
        const count = res.data?.assessments_per_placement ?? 4;
        const interval = res.data?.interval_days ?? 30;
        setAssessmentDefaults({ count, interval });
        const start = new Date(formData.start_date);
        if (Number.isNaN(start.getTime())) return;
        const dates = Array.from({ length: count }, (_, i) => {
          const d = new Date(start);
          d.setDate(d.getDate() + interval * (i + 1));
          return d.toISOString().slice(0, 10);
        });
        setAssessmentDates(dates);
      } catch { /* silently keep defaults; user can still add rows */ }
    })();
    return () => { cancelled = true; };
  }, [formData.student_id, formData.start_date, editingPlacement]);

  // Cohort-aware student filter for the placement modal. When a cycle is
  // selected we filter to students whose full cohort matches (year_of_enrollment
  // + year_of_study + course_length + course_type) — this mirrors the strict
  // validation the backend runs on POST /placements. When no cycle is picked
  // we fall back to the manual year/course filters.
  const filteredStudentsForModal = React.useMemo(() => {
    const cyc = cycles.find(c => c.id === formData.cycle_id);
    return students.filter(s => {
      if (s.status !== 'active') return false;
      if (cyc) {
        if (String(s.year_of_enrollment || '') !== String(cyc.year_of_enrollment || '')) return false;
        if ((s.year_of_study || '') !== (cyc.year_of_study || '')) return false;
        if ((s.course_length || '3 Year') !== (cyc.course_length || '')) return false;
        if ((s.course_type || '') !== (cyc.course_type || '')) return false;
        return true;
      }
      if (filterYear && s.year_of_study !== filterYear) return false;
      if (filterCourse && (s.course_length || '3 Year') !== filterCourse) return false;
      return true;
    });
  }, [students, cycles, formData.cycle_id, filterYear, filterCourse]);

  const toggleRestaurant = (id) => {
    setExpandedRestaurants(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Export scope = the placements currently visible (cycle, status and search filters applied).
  const visiblePlacements = () => restaurantData.flatMap((r) => r.placements);
  const scopeLabel = (n) => `${n} placement${n === 1 ? '' : 's'}${n !== placements.length ? ' (filtered)' : ''}`;

  const handleExportExcel = async () => {
    try {
      const visible = visiblePlacements();
      const res = await placementsAPI.exportExcel(visible.length !== placements.length ? visible.map((p) => p.id) : undefined);
      downloadBlob(res.data, `placements_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Excel export downloaded — ${scopeLabel(visible.length)}`);
    } catch {
      toast.error('Failed to export placements');
    }
  };

  const handleExportPdf = async () => {
    try {
      const columns = ['Student', 'Student #', 'Year', 'Restaurant', 'Position', 'Start', 'End', 'Status', 'Feedback'];
      const sorted = visiblePlacements()
        .slice()
        .sort((a, b) => (a.restaurant_name || '').localeCompare(b.restaurant_name || '') || byStudentName(a, b));
      const studentMap = students.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
      const rows = sorted.map((p) => {
        const s = studentMap[p.student_id] || {};
        return [
          p.student_name || '-',
          s.student_number || '-',
          p.student_year || '-',
          p.restaurant_name || '-',
          p.position || '-',
          p.start_date || '-',
          p.end_date || '-',
          p.status || '-',
          `${p.feedback_completed || 0}/${p.feedback_total || 0}`,
        ];
      });
      await exportTableToPdf({
        title: 'Mizanplaz — Placements',
        subtitle: scopeLabel(rows.length),
        columns,
        rows,
        filename: `placements_${new Date().toISOString().slice(0, 10)}.pdf`,
        orientation: 'landscape',
      });
      toast.success(`PDF export downloaded — ${scopeLabel(rows.length)}`);
    } catch {
      toast.error('Failed to export placements PDF');
    }
  };

  // Filter placements by the currently drilled-in cycle (or the "unassigned"
  // bucket for legacy placements missing a cycle_id).
  const cycleScopedPlacements = React.useMemo(() => {
    if (!cycleParam) return placements;
    if (cycleParam === 'unassigned') return placements.filter((p) => !p.cycle_id);
    return placements.filter((p) => p.cycle_id === cycleParam);
  }, [placements, cycleParam]);

  const unassignedCount = React.useMemo(
    () => placements.filter((p) => !p.cycle_id).length,
    [placements],
  );

  const selectedCycle = React.useMemo(
    () => (cycleParam && cycleParam !== 'unassigned' ? cycles.find((c) => c.id === cycleParam) : null),
    [cycles, cycleParam],
  );

  // Build restaurant-centric data. When a `?status=` filter is active we only
  // show restaurants that have at least one placement matching that status,
  // and auto-expand them so the drilled-down rows are visible immediately.
  // When a cycle is picked we scope restaurants to only those in the cycle's
  // participating list (or those with at least one matching placement for
  // "unassigned").
  const restaurantData = restaurants
    .filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(r => {
      if (!cycleParam) return true;
      if (cycleParam === 'unassigned') {
        return cycleScopedPlacements.some((p) => p.restaurant_id === r.id);
      }
      if (selectedCycle?.restaurant_ids && selectedCycle.restaurant_ids.length > 0) {
        return selectedCycle.restaurant_ids.includes(r.id);
      }
      // Cycle without an explicit restaurant list: fall back to restaurants
      // that actually have placements in this cycle.
      return cycleScopedPlacements.some((p) => p.restaurant_id === r.id);
    })
    .map(r => {
      const rAllPlacements = cycleScopedPlacements.filter(p => p.restaurant_id === r.id);
      const rPlacements = statusFilter
        ? rAllPlacements.filter(p => (p.status || '').toLowerCase() === statusFilter.toLowerCase())
        : rAllPlacements;
      const activePlacements = rAllPlacements.filter(p => p.status === 'active' || p.status === 'pending');
      return {
        ...r,
        placements: rPlacements,
        filledSlots: activePlacements.length,
        totalSlots: r.available_positions || 0,
        openSlots: Math.max((r.available_positions || 0) - activePlacements.length, 0),
      };
    })
    .filter(r => !statusFilter || r.placements.length > 0)
    .sort(byName);

  // Auto-expand all restaurants when a status filter is applied, so the
  // filtered placement rows are immediately visible without extra clicks.
  useEffect(() => {
    if (statusFilter && restaurantData.length > 0) {
      setExpandedRestaurants(prev => {
        const next = { ...prev };
        restaurantData.forEach(r => { next[r.id] = true; });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, placements.length, restaurants.length]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="placements-page" className="space-y-6 animate-fade-in">
        {orphanCount > 0 && (
          <button
            type="button"
            data-testid="orphan-banner"
            onClick={() => setOrphanDialogOpen(true)}
            className="w-full flex items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors px-4 py-3 text-left"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-amber-800">
                  {orphanCount} orphaned placement{orphanCount !== 1 ? 's' : ''} need{orphanCount === 1 ? 's' : ''} attention
                </div>
                <div className="text-xs text-amber-700">
                  Their restaurant has been deleted. Click to reassign to an existing restaurant or delete the record.
                </div>
              </div>
            </div>
            <span className="text-sm font-medium text-amber-700 shrink-0">Fix now →</span>
          </button>
        )}

        <div className="flex items-center justify-between">
          <div>
            {cycleParam ? (
              <>
                <button
                  type="button"
                  data-testid="back-to-cycles"
                  onClick={() => setSearchParams({}, { replace: true })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-ica-navy hover:underline mb-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back to cycles
                </button>
                <h1 className="font-heading text-3xl font-bold text-ica-navy" data-testid="placements-breadcrumb">
                  <span className="text-gray-400 font-normal">Placements ›</span>{' '}
                  {cycleParam === 'unassigned' ? 'Unassigned' : (selectedCycle?.name || 'Cycle')}
                </h1>
                <p className="text-gray-500 mt-1">
                  {restaurantData.length} restaurant{restaurantData.length === 1 ? '' : 's'} | {cycleScopedPlacements.length} placement{cycleScopedPlacements.length === 1 ? '' : 's'}
                  {statusFilter && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-ica-navy/10 text-ica-navy px-2 py-0.5 text-xs font-semibold uppercase">
                      status: {statusFilter}
                      <button
                        type="button"
                        data-testid="clear-status-filter"
                        onClick={() => {
                          const next = new URLSearchParams(searchParams);
                          next.delete('status');
                          setSearchParams(next, { replace: true });
                        }}
                        className="ml-1 hover:text-red-600"
                        title="Clear status filter"
                      >×</button>
                    </span>
                  )}
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-3xl font-bold text-ica-navy">Placements</h1>
                <p className="text-gray-500 mt-1">
                  Pick a placement cycle to see its restaurants and placements.
                </p>
              </>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="bulk-letters-btn"
              variant="outline"
              onClick={() => setBulkLettersOpen(true)}
             
            >
              <Sparkles className="w-4 h-4 mr-2" /> Bulk Letters
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="export-placements-btn"
                  variant="outline"
                 
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-placements-excel" onClick={handleExportExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-placements-pdf" onClick={handleExportPdf}>
                  <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid="add-placement-btn"
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              
            >
              <Plus className="w-4 h-4 mr-2" /> New Placement
            </Button>
          </div>
        </div>

        {!cycleParam ? (
          <CyclePickerGrid
            cycles={cycles}
            placements={placements}
            unassignedCount={unassignedCount}
            onSelect={(cid) => {
              const next = new URLSearchParams(searchParams);
              next.set('cycleId', cid == null ? 'unassigned' : cid);
              setSearchParams(next, { replace: false });
            }}
            testIdPrefix="placements-cycle"
          />
        ) : (
        <>
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-placements"
            placeholder="Search restaurants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Restaurant List */}
        <div className="space-y-3">
          {restaurantData.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">No restaurants found.</CardContent></Card>
          ) : (
            restaurantData.map((r) => {
              const isExpanded = expandedRestaurants[r.id];
              const fillPercent = r.totalSlots > 0 ? Math.min((r.filledSlots / r.totalSlots) * 100, 100) : 0;
              const barColor = fillPercent >= 100 ? 'bg-red-500' : fillPercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500';

              return (
                <Card key={r.id} className="overflow-hidden">
                  {/* Restaurant Row */}
                  <div
                    data-testid={`restaurant-placement-${r.id}`}
                    className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleRestaurant(r.id)}
                  >
                    <div className="flex-shrink-0">
                      {isExpanded
                        ? <ChevronDown className="w-5 h-5 text-ica-navy" />
                        : <ChevronRight className="w-5 h-5 text-gray-400" />}
                    </div>
                    <div className="w-10 h-10 bg-ica-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-ica-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{r.name}</p>
                      <p className="text-xs text-gray-500">{r.address || 'No address'}</p>
                    </div>
                    <div className="flex items-center gap-6 flex-shrink-0">
                      {/* Capacity Bar */}
                      <div className="w-32">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{r.filledSlots}/{r.totalSlots} filled</span>
                          <span className={`font-medium ${r.openSlots > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {r.openSlots} open
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${fillPercent}%` }} />
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Users className="w-4 h-4" />
                        <span>{r.placements.length}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded: Placements at this restaurant */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50/50">
                      {r.placements.length === 0 ? (
                        <div className="px-5 py-6 text-center text-sm text-gray-400">No placements at this restaurant yet.</div>
                      ) : (
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-100/80">
                              <th className="text-left py-2 px-5 text-xs font-semibold text-gray-500 uppercase">Student</th>
                              <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Position</th>
                              <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Period</th>
                              <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                              <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Feedback</th>
                              <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.placements.sort(byStudentName).map((p) => (
                              <tr
                                key={p.id}
                                data-testid={`placement-row-${p.id}`}
                                className="border-t border-gray-100 hover:bg-ica-gold/5 transition-colors cursor-pointer"
                                onClick={(e) => {
                                  if (e.target.closest('button')) return;
                                  setDetailsPlacementId(p.id);
                                }}
                              >
                                <td className="py-3 px-5">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy font-semibold text-xs">
                                      {p.student_name.charAt(0)}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{p.student_name}</p>
                                      <p className="text-xs text-gray-400">{p.student_year}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-sm text-gray-600">{p.position || '-'}</td>
                                <td className="py-3 px-4">
                                  <div className="text-sm text-gray-600">
                                    <div>{p.start_date}</div>
                                    <div className="text-gray-400 text-xs">to {p.end_date}</div>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    p.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                                    : p.status === 'pending' ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-100 text-gray-600'
                                  }`}>{p.status}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                    p.feedback_completed === p.feedback_total && p.feedback_total > 0
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : p.feedback_completed > 0
                                      ? 'bg-blue-50 text-blue-700'
                                      : p.feedback_total > 0
                                      ? 'bg-amber-50 text-amber-700'
                                      : 'bg-gray-50 text-gray-500'
                                  }`}>
                                    <MessageSquare className="w-3 h-3" />
                                    {p.feedback_total > 0 ? `${p.feedback_completed}/${p.feedback_total}` : 'None'}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex gap-1">
                                    <button
                                      data-testid={`placement-contracts-${p.id}`}
                                      onClick={(e) => { e.stopPropagation(); setContractsPlacement(p); }}
                                      className="p-1.5 hover:bg-ica-gold/10 rounded text-ica-gold-dark transition-colors"
                                      title="Student Placement Confirmation Letter"
                                    >
                                      <FileSignature className="w-4 h-4" />
                                    </button>
                                    <button
                                      data-testid={`edit-placement-${p.id}`}
                                      onClick={(e) => { e.stopPropagation(); handleEdit(p); }}
                                      className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
                                      title="Edit placement"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      data-testid={`delete-placement-${p.id}`}
                                      onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                                      className="p-1.5 hover:bg-red-50 text-red-500 rounded transition-colors"
                                      title="Delete placement"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
        </>
        )}
      </div>

      {/* Create/Edit Placement Modal — Placement Modal 2.0 */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) { resetForm(); } setIsModalOpen(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="placement-modal">
          {/* Top action bar — Cancel + Save (+ Save & Send Ack) mirrored here
              so the primary action is always visible without scrolling. */}
          <DialogActionBar>
            <Button variant="outline" size="sm" onClick={() => { resetForm(); setIsModalOpen(false); }} data-testid="cancel-placement-top">Cancel</Button>
            <Button
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={() => handleSubmit({ sendAck: false })}
              data-testid="save-placement-top"
            >
              {editingPlacement ? (saving ? 'Updating…' : 'Update') : (saving ? 'Saving…' : 'Save')}
            </Button>
            {!editingPlacement && (
              <Button
                size="sm"
                variant="accent"
                disabled={saving}
                onClick={() => handleSubmit({ sendAck: true })}
                data-testid="save-and-send-ack-top"
              >
                {saving ? 'Working…' : 'Save & Send Ack'}
              </Button>
            )}
          </DialogActionBar>
          <DialogHeader>
            <DialogTitle>{editingPlacement ? 'Edit Placement' : 'New Placement'}</DialogTitle>
            <DialogDescription>
              {editingPlacement ? 'Update placement details.' : 'Assign a student to a restaurant in this cycle.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Cycle picker — hidden entirely when there is exactly one active cycle
                (already auto-selected by resetForm), otherwise shown as a compact chip
                strip instead of a full-width dropdown so it takes less vertical space. */}
            {!editingPlacement && (() => {
              const activeCycles = cycles.filter(c => c.status !== 'archived' && c.status !== 'closed');
              if (activeCycles.length === 0) {
                return (
                  <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800" data-testid="no-active-cycles-warning">
                    No active cycles — create a Placement Cycle first.
                  </div>
                );
              }
              if (activeCycles.length === 1) {
                const cyc = activeCycles[0];
                return (
                  <div className="rounded-md bg-ica-navy/5 border border-ica-navy/10 px-3 py-2 text-xs text-ica-navy flex items-center justify-between" data-testid="auto-selected-cycle">
                    <div><span className="font-semibold">Cycle:</span> {cyc.name}</div>
                    <span className="text-[10px] text-gray-500">Class of {cyc.year_of_enrollment} · {cyc.year_of_study} · {cyc.course_type}</span>
                  </div>
                );
              }
              return (
                <div>
                  <Label>Placement cycle *</Label>
                  <Select
                    value={formData.cycle_id}
                    onValueChange={(v) => {
                      const cyc = cycles.find((c) => c.id === v);
                      setFormData({
                        ...formData, cycle_id: v, student_id: '', restaurant_id: '',
                        start_date: cyc?.start_date || formData.start_date,
                        end_date: cyc?.end_date || formData.end_date,
                      });
                      if (cyc) { setFilterYear(cyc.year_of_study || ''); setFilterCourse(cyc.course_length || ''); }
                    }}
                  >
                    <SelectTrigger data-testid="placement-cycle"><SelectValue placeholder="Pick the cycle this placement belongs to" /></SelectTrigger>
                    <SelectContent>
                      {activeCycles.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.status})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}

            {/* Student picker — filtered to the cycle cohort, unplaced students float
                to the top, already-placed ones are shown at the bottom disabled with
                a "(already placed)" badge so nothing is silently missing. */}
            <div>
              <RequiredLabel>Student</RequiredLabel>
              {(() => {
                const unplaced = filteredStudentsForModal.filter(s => !placedStudentIdsInCycle.has(s.id));
                const placed = filteredStudentsForModal.filter(s => placedStudentIdsInCycle.has(s.id));
                const opts = [
                  ...unplaced.map(s => ({
                    value: s.id,
                    label: s.name,
                    secondary: `${s.student_number || ''}${s.email ? ` · ${s.email}` : ''}`,
                    search: `${s.name} ${s.student_number || ''} ${s.email || ''}`,
                  })),
                  ...placed.map(s => ({
                    value: s.id,
                    label: s.name,
                    secondary: `${s.student_number || ''}${s.email ? ` · ${s.email}` : ''}`,
                    search: `${s.name} ${s.student_number || ''} ${s.email || ''}`,
                    disabled: true,
                    hint: 'already placed',
                    hintClass: 'bg-amber-100 text-amber-700',
                  })),
                ];
                return (
                  <SearchableSelect
                    value={formData.student_id}
                    onValueChange={(v) => setFormData({ ...formData, student_id: v })}
                    options={opts}
                    placeholder={filteredStudentsForModal.length > 0 ? 'Search by name, student #, or email…' : (formData.cycle_id ? 'No unplaced students match this cycle' : 'Pick a cycle first')}
                    emptyText="No matching student"
                    testId="placement-student"
                    optionTestIdPrefix="student-opt"
                  />
                );
              })()}
            </div>

            {/* Restaurant picker — shows only restaurants attached to the selected cycle,
                with live slot counts inline. Full restaurants are disabled. */}
            <div>
              <RequiredLabel>Restaurant</RequiredLabel>
              {(() => {
                const cyc = cycles.find((c) => c.id === formData.cycle_id);
                const allowedIds = cyc?.restaurant_ids;
                if (formData.cycle_id && cyc && (cyc.restaurant_count || 0) === 0) {
                  return (
                    <SearchableSelect
                      value=""
                      onValueChange={() => {}}
                      options={[]}
                      placeholder="Add restaurants to this cycle first"
                      emptyText="No restaurants attached to this cycle"
                      testId="placement-restaurant"
                    />
                  );
                }
                const filtered = allowedIds ? restaurants.filter((r) => allowedIds.includes(r.id)) : restaurants;
                const sorted = [...filtered].sort((a, b) => {
                  const oa = restaurantSlots[a.id]?.open ?? 0;
                  const ob = restaurantSlots[b.id]?.open ?? 0;
                  if ((oa > 0) !== (ob > 0)) return ob > 0 ? 1 : -1;
                  return a.name.localeCompare(b.name);
                });
                const opts = sorted.map(r => {
                  const s = restaurantSlots[r.id] || { filled: 0, total: 0, open: 0 };
                  const isFull = s.open <= 0;
                  return {
                    value: r.id,
                    label: r.name,
                    secondary: r.address || '',
                    search: `${r.name} ${r.address || ''}`,
                    disabled: isFull,
                    hint: isFull ? 'FULL' : `${s.open} / ${s.total} open`,
                    hintClass: isFull ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700',
                  };
                });
                return (
                  <SearchableSelect
                    value={formData.restaurant_id}
                    onValueChange={(v) => setFormData({ ...formData, restaurant_id: v })}
                    options={opts}
                    placeholder={formData.cycle_id ? 'Search by name or address…' : 'Pick a cycle first'}
                    emptyText="No matching restaurant"
                    testId="placement-restaurant"
                    optionTestIdPrefix="restaurant-opt"
                  />
                );
              })()}
            </div>

            {/* Advanced options — collapsed by default. Contains Position, Notes,
                Start/End dates (auto-filled from cycle), and the Assessment Schedule.
                Admins that just want a quick placement don't have to touch these. */}
            <button
              type="button"
              className="text-xs text-ica-navy hover:underline flex items-center gap-1"
              onClick={() => setShowAdvanced(v => !v)}
              data-testid="toggle-advanced-options"
            >
              {showAdvanced ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {showAdvanced ? 'Hide advanced options' : 'Advanced options (position, dates, notes, assessment schedule)'}
            </button>

            {showAdvanced && (
              <div className="space-y-4 border-l-2 border-ica-navy/10 pl-4" data-testid="advanced-options-panel">
                <div>
                  <Label>Position</Label>
                  <Input data-testid="placement-position" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} placeholder="e.g., Line Cook (defaults to 'Placement')" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <RequiredLabel>Start Date</RequiredLabel>
                    <Input data-testid="placement-start-date" type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
                  </div>
                  <div>
                    <RequiredLabel>End Date</RequiredLabel>
                    <Input data-testid="placement-end-date" type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input data-testid="placement-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional" />
                </div>

                {/* Assessment schedule — only for NEW placements */}
                {!editingPlacement && formData.student_id && formData.start_date && (
                  <div className="border-t pt-4" data-testid="assessment-schedule-editor">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <Label className="text-ica-navy font-semibold">Assessment schedule</Label>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Prefilled from the applicable rule ({assessmentDefaults.count} assessments · {assessmentDefaults.interval}-day interval).
                        </p>
                      </div>
                      <Button type="button" size="sm" variant="outline" data-testid="add-assessment-btn"
                        onClick={() => {
                          const last = assessmentDates[assessmentDates.length - 1];
                          const base = last ? new Date(last) : new Date(formData.start_date);
                          base.setDate(base.getDate() + assessmentDefaults.interval);
                          setAssessmentDates([...assessmentDates, base.toISOString().slice(0, 10)]);
                        }}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {assessmentDates.map((date, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`assessment-row-${i}`}>
                          <span className="w-6 h-6 shrink-0 rounded-full bg-ica-navy text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                          <Input type="date" data-testid={`assessment-due-${i}`} value={date}
                            onChange={(e) => { const next = [...assessmentDates]; next[i] = e.target.value; setAssessmentDates(next); }}
                            className="flex-1" />
                          <Button type="button" size="sm" variant="ghost" data-testid={`remove-assessment-${i}`}
                            onClick={() => setAssessmentDates(assessmentDates.filter((_, idx) => idx !== i))}
                            className="text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      {assessmentDates.length === 0 && (
                        <p className="text-xs text-gray-400 italic text-center py-2">
                          No assessments will be scheduled. Click &quot;Add&quot; to create one.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }} data-testid="cancel-placement-btn">Cancel</Button>
            <Button
              data-testid="save-placement-btn"
              variant={editingPlacement ? 'default' : 'outline'}
              disabled={saving}
              onClick={() => handleSubmit({ sendAck: false })}
            >
              {editingPlacement ? (saving ? 'Updating…' : 'Update') : (saving ? 'Saving…' : 'Save')}
            </Button>
            {!editingPlacement && (
              <Button
                data-testid="save-and-send-ack-btn"
                variant="accent"
                onClick={() => handleSubmit({ sendAck: true })}
                disabled={saving}
              >
                {saving ? 'Working…' : 'Save & Send Ack'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Placement Contracts (Student Placement Confirmation Letter) Modal */}
      <Dialog open={!!contractsPlacement} onOpenChange={(o) => !o && setContractsPlacement(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-ica-navy">
              Student Placement Letter — {contractsPlacement?.student_name}
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {contractsPlacement?.restaurant_name} · {contractsPlacement?.start_date} → {contractsPlacement?.end_date}
            </DialogDescription>
          </DialogHeader>
          {contractsPlacement && (
            <PlacementContractsPanel mode="placement" subjectId={contractsPlacement.id} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractsPlacement(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk Student Placement Letters Wizard */}
      <BulkLettersDialog
        open={bulkLettersOpen}
        onOpenChange={setBulkLettersOpen}
        onCompleted={() => fetchData()}
      />
      {/* Orphan repair modal */}
      <OrphanPlacementsDialog
        open={orphanDialogOpen}
        onOpenChange={setOrphanDialogOpen}
        onResolved={() => fetchData()}
      />
      {/* Placement details modal */}
      <PlacementDetailsDialog
        open={!!detailsPlacementId}
        onOpenChange={(o) => { if (!o) setDetailsPlacementId(null); }}
        placementId={detailsPlacementId}
      />
    </Layout>
  );
};

export default Placements;
